/**
 * The issues tracker, as a table — the catalog list's shape, minus the drag.
 *
 * Same reasoning as the catalog: this is rows of the same six facts, scanned down a column,
 * so it is a React Aria `Table` wearing this app's own `.ctable` classes rather than a stack
 * of cards. What it does *not* borrow is the reorder: a catalog row's `order` is a field a
 * parent feels, and an issue's position is just when it was filed.
 *
 * Both filters are chip groups over the loaded list rather than query clauses (`queries.ts`
 * says why), and neither is on by default. A tracker that hides rows until you notice a
 * pressed chip is a tracker that loses things.
 *
 * Every row carries the two **Copy fix prompt** lanes in a trailing column, and the header
 * carries the same pair for the batch. Quick comes first because it is the common case; the
 * rule for choosing is in `docs/dev/README.md` §The issues loop.
 *
 * The batch reads the rows the filters are *currently showing* and takes the open ones out of
 * those (`docs/dev/admin/03`) — a button on a filtered list that quietly acted on the
 * unfiltered one would be a trap, and one that copied nothing would be worse, so both are
 * disabled when the visible set has nothing open in it.
 */

import { Button, Chip, EmptyState, StatusBadge, SurfaceHeader } from '@atlas-nook/design-system';
import { useState } from 'react';
import {
  Button as AriaButton,
  Cell,
  Column,
  Row,
  Table,
  TableBody,
  TableHeader,
} from 'react-aria-components';
import type { JSX } from 'react';
import { useNavigate } from 'react-router';

import { adminEnv } from '../env.js';
import { useIssues } from '../data/queries.js';
import { ISSUE_AREAS, ISSUE_STATUSES } from '../data/issues.js';
import type { Issue, IssueArea, IssueStatus } from '../data/issues.js';
import { ADMIN_STRINGS as S } from '../strings.js';
import { CopyPromptButton } from './CopyPromptButton.js';
import { fixPromptFor, fixPromptForAll, openIssues } from './fixPrompt.js';
import { NewIssueDialog } from './NewIssueDialog.js';

/**
 * Where an issue stands, as the design system's five states.
 *
 * `wontfix` is `none` rather than `error`: a ruling is not a failure. `fixed` is `needs-ok`
 * because that is exactly what it is — waiting on the person who filed it to look again.
 */
export function issueBadge(status: IssueStatus): 'none' | 'generating' | 'needs-ok' | 'ready' {
  if (status === 'verified') return 'ready';
  if (status === 'fixed') return 'needs-ok';
  if (status === 'fixing') return 'generating';
  return 'none';
}

function toggle<T>(set: Set<T>, value: T): Set<T> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

export function IssuesList(): JSX.Element {
  const navigate = useNavigate();
  const { data: filed, loading } = useIssues();
  const [statuses, setStatuses] = useState<Set<IssueStatus>>(new Set());
  const [areas, setAreas] = useState<Set<IssueArea>>(new Set());
  const [creating, setCreating] = useState(false);

  const env = adminEnv();
  const open = filed.filter((row) => row.status === 'open').length;
  const rows = filed.filter(
    (row) =>
      (statuses.size === 0 || statuses.has(row.status)) &&
      (areas.size === 0 || areas.has(row.area)),
  );
  const batch = openIssues(rows);

  return (
    <>
      <SurfaceHeader
        kicker={S.issues.kicker}
        title={S.issues.title}
        meta={S.issues.meta(filed.length, open)}
        actions={
          <>
            <CopyPromptButton
              text={fixPromptForAll(rows, env, 'quick')}
              label={S.issues.copyAllOpenLabel(batch.length)}
              isDisabled={batch.length === 0}
            >
              {S.issues.copyAllOpen}
            </CopyPromptButton>
            <CopyPromptButton
              text={fixPromptForAll(rows, env, 'thorough')}
              label={S.issues.copyAllOpenThoroughLabel(batch.length)}
              isDisabled={batch.length === 0}
            >
              {S.issues.copyAllOpenThorough}
            </CopyPromptButton>
            <Button tone="primary" onClick={() => setCreating(true)}>
              {S.issues.newIssue}
            </Button>
          </>
        }
      />

      <div className="chiprow" role="group" aria-label={S.issues.filterStatus}>
        {ISSUE_STATUSES.map((status) => (
          <Chip
            key={status}
            pressed={statuses.has(status)}
            count={filed.filter((row) => row.status === status).length}
            onClick={() => setStatuses((was) => toggle(was, status))}
          >
            {S.issues.status[status]}
          </Chip>
        ))}
      </div>

      <div className="chiprow" role="group" aria-label={S.issues.filterArea}>
        {ISSUE_AREAS.map((area) => (
          <Chip
            key={area}
            pressed={areas.has(area)}
            count={filed.filter((row) => row.area === area).length}
            onClick={() => setAreas((was) => toggle(was, area))}
          >
            {S.issues.area[area]}
          </Chip>
        ))}
      </div>

      {!loading && filed.length === 0 ? (
        <EmptyState title={S.issues.empty} />
      ) : rows.length === 0 ? (
        <EmptyState title={S.issues.noneMatch} />
      ) : (
        <div className="ctable-wrap">
          <Table className="ctable" aria-label={S.issues.tableLabel} selectionMode="none">
            <TableHeader>
              <Column id="id" isRowHeader className="ch ch-id">
                {S.issues.columns.id}
              </Column>
              <Column id="area" className="ch">
                {S.issues.columns.area}
              </Column>
              <Column id="kind" className="ch">
                {S.issues.columns.kind}
              </Column>
              <Column id="step" className="ch">
                {S.issues.columns.step}
              </Column>
              <Column id="title" className="ch">
                {S.issues.columns.title}
              </Column>
              <Column id="status" className="ch">
                {S.issues.columns.status}
              </Column>
              <Column id="copy" className="ch ch-act">
                {S.issues.columnCopy}
              </Column>
            </TableHeader>

            <TableBody>
              {rows.map((row) => (
                <IssueRow
                  key={row.id}
                  issue={row}
                  env={env}
                  onOpen={() => void navigate(`/issues/${row.id}`)}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <NewIssueDialog
        open={creating}
        onClose={() => setCreating(false)}
        onFiled={(id) => {
          setCreating(false);
          void navigate(`/issues/${id}`);
        }}
      />
    </>
  );
}

function IssueRow({
  issue,
  env,
  onOpen,
}: {
  issue: Issue;
  env: { useEmulator: boolean };
  onOpen: () => void;
}): JSX.Element {
  return (
    <Row id={issue.id} className="crow">
      <Cell className="cc cc-mono">{issue.id}</Cell>
      <Cell className="cc cc-mono">{S.issues.area[issue.area]}</Cell>
      <Cell className="cc cc-mono">{S.issues.kind[issue.kind]}</Cell>
      <Cell className="cc cc-mono">{issue.step ?? S.common.none}</Cell>
      <Cell className="cc">
        <AriaButton className="clink" onPress={onOpen}>
          {issue.title}
        </AriaButton>
      </Cell>
      <Cell className="cc">
        <StatusBadge status={issueBadge(issue.status)}>{S.issues.status[issue.status]}</StatusBadge>
      </Cell>
      {/* The row's own title button is the only thing that navigates, so these have nothing
          to stop — but they are buttons inside a row, and RAC keeps the press to itself
          either way. */}
      <Cell className="cc cc-act">
        <div className="lanes">
          <CopyPromptButton
            text={fixPromptFor(issue, env, 'quick')}
            label={S.issues.copyPromptFor(issue.id)}
            size="sm"
          />
          <CopyPromptButton
            text={fixPromptFor(issue, env, 'thorough')}
            label={S.issues.copyPromptThoroughFor(issue.id)}
            size="sm"
          >
            {S.issues.copyPromptThorough}
          </CopyPromptButton>
        </div>
      </Cell>
    </Row>
  );
}
